import { z } from 'zod';
import {
    CursorPaginatedMetaSchema,
    PaginatedLinksSchema,
    type CursorPaginatedResponse,
    type CursorPaginatedResponseSchema,
    type EmptyZodShape,
} from '@/api/types/pagination.ts';
import type { DeepPartial } from '@/types/utils.ts';

export interface LenientCursorInvalidItem<Item> {
    item: DeepPartial<Item>;
    issues: string[];
}

export interface LenientCursorPaginatedResult<Item> {
    data: Item[];
    links: z.infer<typeof PaginatedLinksSchema>;
    meta: z.infer<typeof CursorPaginatedMetaSchema>;
    invalidItems: LenientCursorInvalidItem<Item>[];
}

const formatLenientValidationIssuePath = (path: PropertyKey[]): string => {
    if (!path.length) {
        return 'root';
    }

    return path.map(String).join('.');
};

const formatLenientValidationIssue = (issue: {
    code: string;
    message: string;
    path: PropertyKey[];
}): string => {
    return [
        `path=${formatLenientValidationIssuePath(issue.path)}`,
        `code=${issue.code}`,
        `message=${issue.message.replace(/\s+/g, ' ').trim()}`,
    ].join(' ');
};

abstract class BaseApi {
    abstract name: string;

    /**
     * Validate a response payload against a schema.
     * @throws {Error} If the payload is invalid.
     */
    protected _validateResponse<T>(schema: z.ZodSchema<T>, payload: unknown): T {
        const parseResult = schema.safeParse(payload);

        if (!parseResult.success) {
            const msg = parseResult.error.message;

            throw new Error(`${this.name} response validation failed: ${msg}`);
        }

        return parseResult.data;
    }

    /**
     * Validate a cursor paginated response against a schema.
     * @throws {Error} If the response is invalid.
     */
    protected _validateCursorPaginatedResponse<
        T extends z.ZodTypeAny,
        Extra extends z.ZodRawShape = EmptyZodShape
    >(
        schema: CursorPaginatedResponseSchema<T, Extra>,
        payload: unknown
    ): CursorPaginatedResponse<T, Extra> {
        const parseResult = schema.safeParse(payload);

        if (!parseResult.success) {
            const msg = parseResult.error.message;

            throw new Error(`${this.name} cursor paginated response validation failed: ${msg}`);
        }

        return parseResult.data;
    }

    /**
     * Validate a cursor paginated list response item-by-item. Invalid items are
     * dropped so a single malformed item does not discard an entire page — and
     * does not stall cursor pagination. The pagination envelope (meta, links)
     * must still be valid: losing the cursor would be worse than losing items.
     */
    protected _validateCursorPaginatedListLenient<T extends z.ZodTypeAny>(
        itemSchema: T,
        payload: unknown
    ): LenientCursorPaginatedResult<z.infer<T>> {
        const envelopeSchema = z.object({
            data: z.array(z.unknown()),
            links: PaginatedLinksSchema,
            meta: CursorPaginatedMetaSchema,
        });
        const envelopeResult = envelopeSchema.safeParse(payload);
        if (!envelopeResult.success) {
            throw new Error(
                `${this.name} cursor paginated response validation failed: ${envelopeResult.error.message}`
            );
        }

        const validItems: z.infer<T>[] = [];
        const invalidItems: LenientCursorInvalidItem<z.infer<T>>[] = [];

        for (const item of envelopeResult.data.data) {
            const itemResult = itemSchema.safeParse(item);
            if (itemResult.success) {
                validItems.push(itemResult.data);
            } else {
                invalidItems.push({
                    item: item as DeepPartial<z.infer<T>>,
                    issues: itemResult.error.issues.map(formatLenientValidationIssue),
                });
            }
        }

        return {
            data: validItems,
            links: envelopeResult.data.links,
            meta: envelopeResult.data.meta,
            invalidItems,
        };
    }
}

export default BaseApi;
